import * as THREE from 'three';

/**
 * WebXR controller for VR and AR sessions
 */
export class WebXRController {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private cameraRig: THREE.Group; // VR camera rig for movement
  private session: XRSession | null = null;
  private sessionMode: XRSessionMode | null = null;
  private controllers: XRInputSource[] = [];
  private baseSpaceType: XRReferenceSpaceType | null = 'local-floor';
  private sessionEndHandler: (() => void) | null = null;

  // Gamepad control settings
  private movementSpeed: number = 2.0; // Units per second
  private rotationSpeed: number = 1.5; // Radians per second
  private deadzone: number = 0.15; // Ignore small stick movements

  // Rig rotation state (for VR movement)
  private rigYaw: number = 0; // Horizontal rotation
  private rigPitch: number = 0; // Vertical rotation (for reference, not applied to rig)

  // Debug tracking
  private lastButtonStates: Map<string, boolean[]> = new Map();
  private lastDebugLog: number = 0;
  private debugLogInterval: number = 1000; // Log every 1 second

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    cameraRig: THREE.Group
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.cameraRig = cameraRig;
  }

  /**
   * Check if WebXR is supported
   */
  public static isSupported(): boolean {
    return 'xr' in navigator;
  }

  /**
   * Check support on this instance
   */
  private isSupported(): boolean {
    return WebXRController.isSupported();
  }

  /**
   * Start a WebXR session
   */
  public async startSession(mode: XRSessionMode): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('WebXR is not supported on this device');
    }

    try {
      const sessionInit: XRSessionInit = {
        optionalFeatures: [
          'dom-overlay',
          'dom-overlay-for-handheld-ar',
          'hand-tracking',
          'hit-test',
        ],
        domOverlay: mode === 'immersive-ar' ? { root: document.body } : undefined,
      };

      this.session = await navigator.xr!.requestSession(mode, sessionInit);
      this.sessionMode = mode;

      await this.setupSession();
      this.setupRenderer();
      this.updateSessionStatus();

      console.log(`Started ${mode} session`);
    } catch (error) {
      console.error(`Failed to start ${mode} session:`, error);

      throw error;
    }
  }

  /**
   * Setup WebXR session
   */
  private async setupSession(): Promise<void> {
    if (!this.session) {
      throw new Error('No active XR session');
    }

    // Get reference space for local tracking
    try {
      await this.session.requestReferenceSpace('local-floor');
      this.baseSpaceType = 'local-floor';
    } catch (e) {
      console.warn('Could not get local-floor reference space, trying local:', e);
      await this.session.requestReferenceSpace('local');
      this.baseSpaceType = 'local';
    }

    // Setup input sources (controllers)
    this.session.addEventListener('inputsourceschange', (e) => {
      this.handleInputSourcesChange(e);
    });

    // Listen for session end
    this.sessionEndHandler = () => {
      this.onSessionEnded();
    };
    this.session.addEventListener('end', this.sessionEndHandler as EventListener);

    // Add camera to rig for VR mode
    // Store camera's current position relative to world before parenting
    const worldPosition = new THREE.Vector3();
    const worldQuaternion = new THREE.Quaternion();
    this.camera.getWorldPosition(worldPosition);
    this.camera.getWorldQuaternion(worldQuaternion);

    // Parent camera to rig
    this.cameraRig.add(this.camera);

    // Reset camera's local transform so headset tracking works naturally
    this.camera.position.set(0, 0, 0);
    this.camera.quaternion.identity();

    console.log('WebXR session setup complete');
  }

  /**
   * Setup renderer for WebXR
   */
  private setupRenderer(): void {
    if (!this.session) return;
    this.renderer.xr.enabled = true;
    this.renderer.xr.setReferenceSpaceType(this.baseSpaceType || 'local-floor');
    // Bind the XR session to Three.js renderer
    // @ts-ignore - setSession exists on WebXRManager
    this.renderer.xr.setSession(this.session);
    if (this.sessionMode === 'immersive-vr') {
      // Mild foveation for performance
      this.renderer.xr.setFoveation?.(1);
    }

    // Use XR-driven animation loop
    this.renderer.setAnimationLoop(() => {
      this.updateControllers();
      this.renderer.render(this.scene, this.camera);
    });
  }

  /**
   * Handle input source changes
   */
  private handleInputSourcesChange(event: XRInputSourcesChangeEvent): void {
    console.log('Input sources changed', event);
    if (event.added) {
      event.added.forEach((source) => {
        console.log(
          'Added controller:',
          source.handedness,
          source.gamepad ? 'with gamepad' : 'no gamepad'
        );
      });
    }
    if (event.removed) {
      event.removed.forEach((source) => {
        console.log('Removed controller:', source.handedness);
      });
    }
  }

  /**
   * Update controller inputs and handle gamepad events
   */
  private updateControllers(): void {
    if (!this.session) return;

    const frame = this.renderer.xr.getFrame();
    if (!frame) return;

    const inputSources = this.session.inputSources;
    let leftGamepad: Gamepad | null = null;
    let rightGamepad: Gamepad | null = null;

    // Get gamepads from input sources
    for (const source of inputSources) {
      if (source.gamepad) {
        if (source.handedness === 'left') {
          leftGamepad = source.gamepad;
        } else if (source.handedness === 'right') {
          rightGamepad = source.gamepad;
        }
      }
    }

    // Calculate delta time (assuming 90fps or 72fps VR)
    const deltaTime = 1 / 90;

    // Debug logging
    // this.debugControllers(leftGamepad, rightGamepad);

    // Process left stick - camera position controls
    if (leftGamepad) {
      this.handlePositionControl(leftGamepad, deltaTime);
    }

    // Process right stick - camera direction controls
    if (rightGamepad) {
      this.handleDirectionControl(rightGamepad, deltaTime);
    }
  }

  /**
   * Handle camera rig position control with left stick
   */
  private handlePositionControl(gamepad: Gamepad, deltaTime: number): void {
    if (!gamepad.axes || gamepad.axes.length < 2) return;

    // Left stick axes (typically axes[2] and axes[3] for left stick)
    const axisX = -gamepad.axes[2];
    const axisY = -gamepad.axes[3];

    // Debug: Log stick movement
    console.log(`[VR] LEFT STICK: X=${axisX.toFixed(2)}, Y=${axisY.toFixed(2)}`);

    // Calculate movement in rig's local space
    const movement = new THREE.Vector3();
    const rigDirection = new THREE.Vector3();
    this.cameraRig.getWorldDirection(rigDirection);

    // Right vector (perpendicular to rig direction)
    const right = new THREE.Vector3();
    right.crossVectors(rigDirection, new THREE.Vector3(0, 1, 0)).normalize();

    // Forward vector (on XZ plane, no vertical component)
    const forward = new THREE.Vector3();
    forward.copy(rigDirection);
    forward.y = 0;
    forward.normalize();

    // Apply movement to the rig
    movement.addScaledVector(right, axisX * this.movementSpeed * deltaTime);
    movement.addScaledVector(forward, -axisY * this.movementSpeed * deltaTime);

    this.cameraRig.position.add(movement);
  }

  /**
   * Handle camera rig direction control with right stick
   * Uses quaternions with up vector constrained to camera's current orientation
   * This accounts for head tracking rotation
   */
  private handleDirectionControl(gamepad: Gamepad, deltaTime: number): void {
    if (!gamepad.axes || gamepad.axes.length < 4) return;

    // Right stick axes (typically axes[2] and axes[3])
    const axisX = gamepad.axes[2];
    const axisY = gamepad.axes[3];

    if (axisX === 0 && axisY === 0) return;

    // Debug: Log stick movement
    console.log(`[VR] RIGHT STICK: X=${axisX.toFixed(2)}, Y=${axisY.toFixed(2)}`);

    // Get camera's coordinate frame in world space
    // This accounts for the user's head rotation from tracking
    const upVector = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);

    // Yaw rotation around camera's up vector (left/right rotation - accounts for head tilt)
    const yawQuat = new THREE.Quaternion();
    yawQuat.setFromAxisAngle(upVector, -axisX * this.rotationSpeed * deltaTime);

    // Apply yaw rotation to rig quaternion
    this.cameraRig.quaternion.multiplyQuaternions(yawQuat, this.cameraRig.quaternion);
  }

  /**
   * End the WebXR session
   */
  public async endSession(): Promise<void> {
    if (this.session) {
      try {
        await this.session.end();
      } catch (e) {
        console.warn('Error ending XR session:', e);
      }
    }

    // Cleanup renderer XR state
    this.renderer.setAnimationLoop(null);
    this.renderer.xr.enabled = false;

    // Remove camera from rig if it's still parented
    if (this.camera.parent === this.cameraRig) {
      // Get camera's world position before removing from rig
      const worldPosition = new THREE.Vector3();
      const worldQuaternion = new THREE.Quaternion();
      this.camera.getWorldPosition(worldPosition);
      this.camera.getWorldQuaternion(worldQuaternion);

      // Remove from rig
      this.cameraRig.remove(this.camera);

      // Restore world transform
      this.camera.position.copy(worldPosition);
      this.camera.quaternion.copy(worldQuaternion);
    }

    this.session = null;
    this.sessionMode = null;
    this.baseSpaceType = null;
    this.updateSessionStatus();
    console.log('WebXR session ended');
  }

  /**
   * Check if a session is active
   */
  public isSessionActive(): boolean {
    return this.session !== null;
  }

  /**
   * Render frame for WebXR
   */
  public render(deltaTime: number): void {
    // No-op: rendering handled by XR animation loop when session is active
    return;
  }

  /**
   * Update session status in UI
   */
  private updateSessionStatus(): void {
    const sessionStatusElement = document.getElementById('session-status');
    if (sessionStatusElement) {
      sessionStatusElement.textContent = this.isSessionActive()
        ? this.sessionMode || 'Active'
        : '-';
    }

    // Update toggle VR button label
    const btnToggleVR = document.getElementById('btn-toggle-vr');
    if (btnToggleVR) {
      btnToggleVR.textContent = this.isSessionActive() ? 'Exit VR' : 'Enter VR';
    }
  }

  /**
   * Dispose resources
   */
  public dispose(): void {
    if (this.session) {
      this.endSession().catch(console.error);
    }
    if (this.session && this.sessionEndHandler) {
      this.session.removeEventListener('end', this.sessionEndHandler as EventListener);
      this.sessionEndHandler = null;
    }
    this.controllers = [];
  }

  /**
   * Toggle VR session on/off
   */
  public async toggleVR(): Promise<void> {
    if (this.isSessionActive()) {
      await this.endSession();
    } else {
      await this.startSession('immersive-vr');
      console.log('Active scene objects:', this.scene.children.length);
      console.log('Camera position:', this.camera.position);
    }
  }

  /**
   * Internal handler when XR session ends
   */
  private onSessionEnded(): void {
    this.renderer.setAnimationLoop(null);
    this.renderer.xr.enabled = false;

    // Remove camera from rig and restore it to the scene
    if (this.camera.parent === this.cameraRig) {
      // Get camera's world position before removing from rig
      const worldPosition = new THREE.Vector3();
      const worldQuaternion = new THREE.Quaternion();
      this.camera.getWorldPosition(worldPosition);
      this.camera.getWorldQuaternion(worldQuaternion);

      // Remove from rig
      this.cameraRig.remove(this.camera);

      // Restore world transform
      this.camera.position.copy(worldPosition);
      this.camera.quaternion.copy(worldQuaternion);
    }

    this.session = null;
    this.sessionMode = null;
    this.baseSpaceType = null;
    this.updateSessionStatus();
  }
}
